"use client";

import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { Select } from "@package/subframe/components/Select";
import { TextField } from "@package/subframe/components/TextField";
import { FeatherX } from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  useCreateRoomCommandControllerCreate,
  useUpdateRoomCommandControllerUpdate,
} from "@/generated/rooms/rooms";

const roomSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Room name is required")
    .max(100, "Name must be less than 100 characters"),
});

interface RoomFormDialogProps {
  nurseryId: string;
  room?: { id: string; name: string; isActive: boolean } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RoomFormDialog({
  nurseryId,
  room,
  open,
  onOpenChange,
}: RoomFormDialogProps) {
  const queryClient = useQueryClient();
  const mode = room ? "edit" : "create";

  const [name, setName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (room && mode === "edit") {
      setName(room.name);
      setIsActive(room.isActive);
    }
  }, [room, mode]);

  useEffect(() => {
    if (!open || mode === "create") {
      setName("");
      setIsActive(true);
      setErrors({});
    }
  }, [open, mode]);

  const { mutate: createRoom, isPending: isCreating } =
    useCreateRoomCommandControllerCreate({
      mutation: {
        onSuccess: async () => {
          toast.success("Room created successfully");
          await queryClient.invalidateQueries({
            predicate: (query) =>
              Array.isArray(query.queryKey) &&
              typeof query.queryKey[0] === "string" &&
              query.queryKey[0].includes("/rooms"),
            refetchType: "all",
          });
          onOpenChange(false);
        },
        onError: (error: unknown) => {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to create room";
          toast.error(errorMessage);
        },
      },
    });

  const { mutate: updateRoom, isPending: isUpdating } =
    useUpdateRoomCommandControllerUpdate({
      mutation: {
        onSuccess: async () => {
          toast.success("Room updated successfully");
          await queryClient.invalidateQueries({
            predicate: (query) =>
              Array.isArray(query.queryKey) &&
              typeof query.queryKey[0] === "string" &&
              query.queryKey[0].includes("/rooms"),
            refetchType: "all",
          });
          onOpenChange(false);
        },
        onError: (error: unknown) => {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to update room";
          toast.error(errorMessage);
        },
      },
    });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const result = roomSchema.safeParse({ name });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.issues.forEach((err) => {
        fieldErrors[err.path.join(".")] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setErrors({});

    if (mode === "create") {
      createRoom({ nurseryId, data: { name, isActive } });
    } else if (room) {
      updateRoom({ nurseryId, roomId: room.id, data: { name, isActive } });
    }
  };

  const isPending = isCreating || isUpdating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="max-w-md max-h-[90vh] overflow-y-auto p-0"
      >
        <DialogTitle className="sr-only">
          {mode === "edit" ? "Edit Room" : "Add Room"}
        </DialogTitle>

        <form onSubmit={handleSubmit} className="flex flex-col">
          <div className="flex items-center justify-between border-b border-solid border-secondary-beige-200 px-6 py-4">
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                {mode === "edit" ? "Edit Room" : "Add Room"}
              </span>
              <span className="text-body font-body text-subtext-color">
                {mode === "edit"
                  ? "Update room name."
                  : "Create a new room for this nursery."}
              </span>
            </div>
            <IconButton
              variant="neutral-tertiary"
              size="medium"
              icon={<FeatherX />}
              onClick={() => onOpenChange(false)}
            />
          </div>

          <div className="flex flex-col gap-6 px-6 py-6">
            <TextField
              className="h-auto w-full"
              size="large"
              label="Room name"
              error={!!errors.name}
              helpText={errors.name}
            >
              <TextField.Input
                placeholder="e.g., Baby Room"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isPending}
              />
            </TextField>

            <Select
              label="Status"
              value={isActive ? "active" : "inactive"}
              onValueChange={(v) => setIsActive(v === "active")}
              disabled={isPending}
            >
              <Select.Item value="active">Active</Select.Item>
              <Select.Item value="inactive">Inactive</Select.Item>
            </Select>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-solid border-secondary-beige-200 px-6 py-4">
            <Button
              type="button"
              variant="neutral-primary"
              size="large"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="brand-primary"
              size="large"
              disabled={isPending}
            >
              {isPending
                ? mode === "edit"
                  ? "Saving..."
                  : "Creating..."
                : mode === "edit"
                  ? "Save Changes"
                  : "Create Room"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
